import 'react-image-crop/dist/ReactCrop.css'

import { Button, Stack, styled, Typography } from '@mui/material'
import { FC, SyntheticEvent, useCallback, useRef, useState } from 'react'
import ReactCrop, { Crop } from 'react-image-crop'
import { IconButton } from 'utoniq-ui'

import { BaseLayout } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FullscreenModal } from '@/components/organisms/functionals'
import { useMessage, useTranslation } from '@/core'
import { useDeviceInfo } from '@/core/native/device'

type ImageEditModalProps = {
  open: boolean
  onClose: () => void
  src: string
  ratio?: number
  onSave: (data: { tmpPath: string; fileUrl: string }, localFile: Blob) => void
  onUploadFile: (
    file: Blob | File
  ) => Promise<{ tmpPath: string; fileUrl: string }>
  isConsole?: boolean
}

export const ImageEditModal: FC<ImageEditModalProps> = ({
  open,
  src,
  onClose,
  ratio = 375 / 281,
  onSave,
  onUploadFile,
  isConsole = false,
}) => {
  const { t } = useTranslation('threadCreate')
  const { deviceInfo } = useDeviceInfo()
  const [saving, setSaving] = useState<boolean>(false)
  const { showError } = useMessage()
  const imageRef = useRef<HTMLImageElement>()
  const cropRef = useRef<Crop>()
  const [crop, setCrop] = useState<Crop>({
    x: 0,
    y: 0,
    width: 100,
    height: 100 / ratio,
    unit: '%',
  })

  const handleImageLoaded = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      imageRef.current = event.currentTarget
      const newCrop = getCropBySize(
        event.currentTarget.width,
        event.currentTarget.height,
        ratio
      )
      cropRef.current = newCrop
      setCrop(newCrop)
      return false
    },
    [ratio]
  )

  const handleCropImage = useCallback(async () => {
    if (!imageRef.current || !cropRef.current) {
      return
    }
    setSaving(true)
    try {
      const newCrop = convertCropToPx(imageRef.current, cropRef.current)
      const localFile = await getCroppedImg(
        imageRef.current,
        newCrop,
        deviceInfo?.operatingSystem === 'ios'
      )
      const data = await onUploadFile(localFile)
      onSave(data, localFile)
    } catch (err) {
      /**
       * @todo エラーハンドリングの型付け
       * https://app.clickup.com/t/908127/UT-5180
       */
      if (typeof err === 'string') {
        showError(new Error(t(err)))
      } else {
        showError(err as Error)
      }
    }
    setSaving(false)
  }, [deviceInfo?.operatingSystem, onUploadFile, onSave, showError, t])

  const Layout = isConsole ? ConsoleBaseLayout : BaseLayout

  return (
    <FullscreenModal open={open} onClose={() => onClose()}>
      <Layout
        header={{
          left: <IconButton.Back edge="start" onClick={onClose} />,
          title: t('imageEditTitle'),
          right: (
            <Button
              loading={saving}
              color="primary"
              sx={{ mr: -1 }}
              onClick={handleCropImage}
            >
              {t('keep')}
            </Button>
          ),
        }}
      >
        <Stack
          flex={1}
          justifyContent="center"
          sx={{ height: '100%', backgroundColor: 'black' }}
        >
          <ReactCropStyled
            crop={crop}
            aspect={ratio}
            onChange={newCrop => {
              setCrop(newCrop)
              cropRef.current = newCrop
            }}
            renderSelectionAddon={() => (
              <Typography variant="body2" align="center" color="white">
                {t('imageEditDesc')}
              </Typography>
            )}
          >
            <img src={src} alt="" onLoad={handleImageLoaded} />
          </ReactCropStyled>
        </Stack>
      </Layout>
    </FullscreenModal>
  )
}

export function getCropBySize(
  width: number,
  height: number,
  ratio: number
): Crop {
  const newWidth =
    width / ratio < height * ratio ? 100 : ((height * ratio) / width) * 100
  const newHeight =
    width / ratio > height * ratio ? 100 : (width / ratio / height) * 100
  const y = (100 - newHeight) / 2
  const x = (100 - newWidth) / 2
  return {
    unit: '%',
    width: newWidth,
    height: newHeight,
    x,
    y,
  }
}

function getCroppedImg(
  image: HTMLImageElement,
  crop: Crop,
  isIOS: boolean,
  // iOSのCanvasで描画できるピクセル面積の限界の1/4のサイズを画像の許容値とし、それより大きな画像はこれに収まるようにリサイズする
  maximumPixels = 4096 * 4096 * 0.25
): Promise<Blob> {
  const canvas = document.createElement('canvas')
  const pixelRatio = window.devicePixelRatio
  const scaleX = image.naturalWidth / image.width
  const scaleY = image.naturalHeight / image.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return Promise.reject('Can not get context')
  }

  const requiredPixels =
    image.naturalWidth * pixelRatio * image.naturalHeight * pixelRatio
  let scalar = 1
  // リサイズ係数の算出
  if (requiredPixels > maximumPixels) {
    scalar = Math.sqrt(maximumPixels) / Math.sqrt(requiredPixels)
  }

  canvas.width = crop.width * pixelRatio * scaleX * scalar
  canvas.height = crop.height * pixelRatio * scaleY * scalar

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  ctx.imageSmoothingQuality = 'high'

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX * scalar,
    crop.height * scaleY * scalar
  )
  // As a blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          return reject(
            /**
             * @todo
             * iOSサイズ制限のため縦横幅が大きい画像をcanvasで出力できない。トリムなしで画像アップできるようにリサイズ処理などを実装する。
             *
             * https://app.clickup.com/t/908127/UT-5179
             */
            crop.width * scaleX > 2355 ? 'canvasSizeOver' : 'canvasIsEmpty'
          )
        }
        return resolve(blob)
      },
      'image/jpeg',
      1
    )
  })
}

export function convertCropToPx(img: HTMLImageElement, crop: Crop): Crop {
  if (crop.unit === 'px') {
    return { ...crop }
  }
  return {
    ...crop,
    unit: 'px',
    width: (crop.width * img.width) / 100,
    height: (crop.height * img.height) / 100,
    x: (crop.x * img.width) / 100,
    y: (crop.y * img.height) / 100,
  }
}

const ReactCropStyled = styled(ReactCrop)(() => ({
  '&&': {
    '.ReactCrop__drag-handle': {
      margin: 0,
      '&.ord-nw': {
        '&::after': {
          borderTopWidth: 4,
          borderLeftWidth: 4,
        },
      },
      '&.ord-ne': {
        '&::after': {
          borderTopWidth: 4,
          borderRightWidth: 4,
        },
      },
      '&.ord-sw': {
        '&::after': {
          borderBottomWidth: 4,
          borderLeftWidth: 4,
        },
      },
      '&.ord-se': {
        '&::after': {
          borderBottomWidth: 4,
          borderRightWidth: 4,
        },
      },
      '&::after': {
        width: 40,
        height: 40,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'white',
        backgroundColor: 'transparent',
      },
    },
    '& .ReactCrop__crop-selection': {
      borderStyle: 'solid',
      animation: 'none',
      backgroundImage: 'none',
      borderWidth: 2,
    },
    '.ReactCrop__selection-addon': {
      position: 'absolute',
      bottom: -50,
      left: 0,
      right: 0,
      width: '100%',
    },
  },
}))
